import {
  EuiCard,
  EuiText,
  EuiIcon,
  EuiCheckbox,
  EuiButtonIcon,
  EuiSplitPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiCardProps,
} from "@equipmentshare/ds2";
import { useIdentity } from "@equipmentshare/auth0-react";

import React, { ReactNode, useEffect, useState } from "react";
import { useLocalStorage, useUnmount } from "react-use";

import { useDispatch } from "~/Action";
import { Time } from "~/Time";

export function DriverTimezoneMismatchWarning({
  driverZone: driverZone,
}: {
  readonly driverZone?: Time.Zone;
}) {
  const { userId } = useIdentity();

  const currentZone = Time.Zone.use();
  const [originalZone] = useState(currentZone);
  const [
    showMismatchPreference,
    setMismatchPreference,
    removeMismatchPreference,
  ] = useLocalStorage<boolean>(`showDriverTZMismatch-${userId}`);

  const dispatch = useDispatch();
  const [mismatchOccurred, setMismatchOccurred] = useState(false);
  const [showTimezoneWarning, shouldShowTimezoneWarning] = useState(false);
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    if (
      !mismatchOccurred &&
      driverZone &&
      currentZone.zone == originalZone.zone &&
      driverZone.zone !== originalZone.zone
    ) {
      shouldShowTimezoneWarning(showMismatchPreference !== false);
      dispatch(Time.Zone.Action.create(driverZone));
      setMismatchOccurred(true);

      setTimer(setTimeout(() => shouldShowTimezoneWarning(false), 10 * 1000));
    }
  }, [currentZone.zone, driverZone?.zone]);

  useUnmount(() => {
    timer && clearTimeout(timer);

    if (
      mismatchOccurred &&
      currentZone.zone == driverZone?.zone &&
      currentZone.zone !== originalZone.zone
    ) {
      dispatch(Time.Zone.Action.create(originalZone));
    }
  });

  const [checked, setChecked] = useState<boolean>(false);
  return showTimezoneWarning ? (
    <HorizontalCard
      data-testid="mismatch-card"
      style={{
        top: 12,
        right: 24,
        position: "absolute",
        zIndex: 1,
      }}
      title={
        <EuiFlexGroup
          direction="row"
          alignItems="center"
          justifyContent="spaceBetween"
          gutterSize="s"
        >
          <EuiFlexItem>
            <p>You are now viewing Driver’s time zone.</p>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButtonIcon
              aria-label="close"
              size="s"
              color="text"
              iconType="cross"
              iconSize="l"
              onClick={() => {
                shouldShowTimezoneWarning(false);
                timer && clearTimeout(timer);
              }}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      }
      icon={<EuiIcon type="clock" size="xl" />}
      footer={
        <EuiCheckbox
          id="dontShowAgainCheckbox"
          label="Do not show me this message again."
          checked={checked}
          onChange={() => {
            !checked
              ? setMismatchPreference(false)
              : removeMismatchPreference();
            setChecked(!checked);
          }}
        />
      }
    >
      <EuiText grow={false} style={{ maxWidth: 450 }}>
        <p>
          You can change your viewing time zone at any point with the time zone
          selector above.
        </p>
      </EuiText>
    </HorizontalCard>
  ) : null;
}

export type HorizontalCardProps = {
  readonly footer?: ReactNode;
} & Omit<
  EuiCardProps,
  "layout" | "display" | "paddingSize" | "footer" | "image" | "textAlign"
>;

/**
 * HorizontalCard wraps an EuiCard in horizontal mode and appends
 * a footer element at the bottom of the card. This is a work around
 * for EuiCard not supporting horizontal cards with footers.
 */
export function HorizontalCard({
  footer,
  style,
  children,
  ...props
}: HorizontalCardProps) {
  return (
    <EuiSplitPanel.Outer style={style}>
      <EuiSplitPanel.Inner>
        <EuiCard
          paddingSize="none"
          display="plain"
          layout="horizontal"
          {...props}
        >
          {children}
        </EuiCard>
      </EuiSplitPanel.Inner>
      {footer && <EuiSplitPanel.Inner>{footer}</EuiSplitPanel.Inner>}
    </EuiSplitPanel.Outer>
  );
}
